/**
 * Service Form Component
 * 
 * A comprehensive form component for creating and editing church services.
 * Handles all aspects of a service including:
 * - Basic service information (title, date, category)
 * - Service timing details
 * - Rehearsal scheduling
 * - Song selection and management
 * - Team member assignments
 * 
 * Uses Material-UI components for the interface and manages complex form state
 * including nested objects for rehearsal and team information.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, TextField, Grid, 
  Typography, IconButton, Paper, Divider, useTheme, useMediaQuery, alpha, Tooltip,
  Chip, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { 
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as LeaderIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import SongSelector from './SongSelector';
import ServiceSongList from './ServiceSongList';
import TeamScheduler from './TeamScheduler';
import VideoModal from '../songs/VideoModal';
import { format, parseISO } from 'date-fns';
import { NOTES } from '../../utils/musicUtils';
import { transposeChordChart, getSemitones } from '../../utils/musicUtils';
import { useAuth } from '../../context/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
// Toast notifications removed as requested
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';

const getRoleIcon = (role) => {
  switch (role?.toLowerCase()) {
    case 'admin':
      return { icon: <AdminIcon fontSize="small" />, color: '#7B1FA2', tooltip: 'Admin' };
    case 'leader':
      return { icon: <LeaderIcon fontSize="small" />, color: '#1976D2', tooltip: 'Leader' };
    case 'member':
    case 'musician':
    case 'guest':
      return { icon: <PersonIcon fontSize="small" />, color: '#757575', tooltip: 'Member' };
    default:
      return { icon: <PersonIcon fontSize="small" />, color: '#757575', tooltip: 'Member' };
  }
};

const ServiceForm = ({ open, onClose, onSubmit, customFetch, initialData, readOnly = false, onServiceUpdate }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { user, canEdit } = useAuth();
  const { t } = useTranslation();
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [memberToDecline, setMemberToDecline] = useState(null);

  const pulseAnimation = {
    '@keyframes pulse': {
      '0%': {
        boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.4)',
      },
      '70%': {
        boxShadow: '0 0 0 10px rgba(76, 175, 80, 0)',
      },
      '100%': {
        boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)',
      },
    },
  };

  const pendingPulseAnimation = {
    '@keyframes pendingPulse': {
      '0%': {
        boxShadow: '0 0 0 0 rgba(255, 152, 0, 0.4)',
      },
      '70%': {
        boxShadow: '0 0 0 6px rgba(255, 152, 0, 0)',
      },
      '100%': {
        boxShadow: '0 0 0 0 rgba(255, 152, 0, 0)',
      },
    },
  };

  const declinedPulseAnimation = {
    '@keyframes declinedPulse': {
      '0%': {
        boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.4)',
      },
      '70%': {
        boxShadow: '0 0 0 6px rgba(244, 67, 54, 0)',
      },
      '100%': {
        boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)',
      },
    },
  };

  const defaultFormData = {
    title: '',
    date: null,
    time: {
      start: new Date(`1970-01-01T00:00:00`),
      end: new Date(`1970-01-01T01:00:00`)
    },
    category: 'Sunday',
    description: '',
    rehearsal: {
      date: null,
      time: {
        start: new Date(`1970-01-01T00:00:00`),
        end: new Date(`1970-01-01T01:00:00`)
      },
      notes: ''
    },
    songs: [],
    team: []
  };
  
  const [formData, setFormData] = useState(defaultFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  const [lastSavedField, setLastSavedField] = useState(null);
  const [showSaveAnimation, setShowSaveAnimation] = useState(false);
  const [saveAnimationPosition, setSaveAnimationPosition] = useState({ top: 0, left: 0 });

  const saveAnimation = {
    '@keyframes fadeInOut': {
      '0%': {
        opacity: 0,
        transform: 'translateY(10px)',
      },
      '20%': {
        opacity: 1,
        transform: 'translateY(0)',
      },
      '80%': {
        opacity: 1,
        transform: 'translateY(0)',
      },
      '100%': {
        opacity: 0,
        transform: 'translateY(-10px)',
      },
    },
  };

  const SavedIndicator = () => (
    <Box
      sx={{
        position: 'fixed',
        ...saveAnimationPosition,
        zIndex: 9999,
        pointerEvents: 'none',
        ...saveAnimation,
        animation: showSaveAnimation ? 'fadeInOut 1.5s forwards' : 'none',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          px: 2,
          py: 1,
          bgcolor: alpha(theme.palette.success.main, 0.9),
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Typography variant="body2">{t('common.saved')}</Typography>
      </Paper>
    </Box>
  );

  // Function to show save animation near an element
  const showSavedAnimation = (event) => {
    try {
      let element;
      
      if (event?.target && typeof event.target.getBoundingClientRect === 'function') {
        // For input fields, use the event target
        element = event.target;
      } else if (event?.domElement && typeof event.domElement.getBoundingClientRect === 'function') {
        // For drag and drop, use the provided DOM element
        element = event.domElement;
      } else {
        // Fallback to the songs section if no specific element
        element = document.querySelector('[data-section="songs"]');
        
        // If that's not available, try the team section
        if (!element || typeof element.getBoundingClientRect !== 'function') {
          element = document.querySelector('[data-section="team"]');
        }
        
        // Final fallback - just use the form container
        if (!element || typeof element.getBoundingClientRect !== 'function') {
          element = document.querySelector('.MuiDialog-paper');
        }
      }

      // If we still don't have a valid element, just show the animation in the center of the screen
      if (!element || typeof element.getBoundingClientRect !== 'function') {
        setSaveAnimationPosition({
          top: window.innerHeight / 2 - 40,
          left: window.innerWidth / 2 - 30,
        });
      } else {
        const rect = element.getBoundingClientRect();
        setSaveAnimationPosition({
          top: rect.top - 40,
          left: rect.left + (rect.width / 2) - 30,
        });
      }
      
      setShowSaveAnimation(true);
      setTimeout(() => setShowSaveAnimation(false), 1500);
    } catch (error) {
      console.error('Error showing save animation:', error);
      // Still show the animation in the center of the screen if there's an error
      setSaveAnimationPosition({
        top: window.innerHeight / 2 - 40,
        left: window.innerWidth / 2 - 30,
      });
      setShowSaveAnimation(true);
      setTimeout(() => setShowSaveAnimation(false), 1500);
    }
  };

  // Helper function to safely parse date strings
  const safelyParseDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date string: ${dateString}`);
        return null;
      }
      return date;
    } catch (error) {
      console.error(`Error parsing date: ${dateString}`, error);
      return null;
    }
  };

  // Helper to parse time strings like "09:00" or "HH:mm" into Date objects for TimePicker
  const parseTimeString = (timeStr, fallback) => {
    if (!timeStr) return fallback;
    // If it's already a valid Date object
    if (timeStr instanceof Date && !isNaN(timeStr.getTime())) return timeStr;
    // If it's a time string like "09:00" or "14:30"
    if (typeof timeStr === 'string' && /^\d{1,2}:\d{2}$/.test(timeStr)) {
      const d = new Date(`1970-01-01T${timeStr.padStart(5, '0')}:00`);
      if (!isNaN(d.getTime())) return d;
    }
    // If it's an ISO string or other date format
    const d = new Date(timeStr);
    if (!isNaN(d.getTime())) return d;
    return fallback;
  };

  useEffect(() => {
    if (initialData) {
      console.log('Initial service data:', initialData);
      
      // Default time values
      const defaultStartTime = new Date(`1970-01-01T00:00:00`);
      const defaultEndTime = new Date(`1970-01-01T01:00:00`);
      
      // Parse service time values (server stores as strings like "09:00")
      const serviceStartTime = parseTimeString(initialData.time?.start, defaultStartTime);
      const serviceEndTime = parseTimeString(initialData.time?.end, defaultEndTime);
      
      // Parse rehearsal time values
      const rehearsalStartTime = parseTimeString(initialData.rehearsal?.time?.start, defaultStartTime);
      const rehearsalEndTime = parseTimeString(initialData.rehearsal?.time?.end, defaultEndTime);
      
      setFormData({
        title: initialData.title || '',
        date: safelyParseDate(initialData.date),
        time: {
          start: serviceStartTime,
          end: serviceEndTime
        },
        category: initialData.category || 'Sunday',
        description: initialData.description || '',
        rehearsal: {
          date: safelyParseDate(initialData.rehearsal?.date),
          time: {
            start: rehearsalStartTime,
            end: rehearsalEndTime
          },
          notes: initialData.rehearsal?.notes || ''
        },
        songs: initialData.songs || [],
        // Transform team members data to match ServiceMember model
        team: (initialData.team || []).map(member => ({
          _id: member._id,
          user: member.user,
          position: member.position || '',
          customPosition: member.customPosition || '',
          status: member.status || 'pending',
          declineReason: member.declineReason || '',
          role: member.role || 'musician'
        }))
      });
    } else {
      setFormData(defaultFormData);
    }
  }, [initialData]);

  const formatTime = (date) => {
    if (!date) return '';
    // Handle both Date objects and ISO strings
    const dateObj = date instanceof Date ? date : parseISO(date);
    return format(dateObj, 'h:mm a');
  };

  const formatDate = (date) => {
    if (!date) return '';
    // Handle both Date objects and ISO strings
    const dateObj = date instanceof Date ? date : parseISO(date);
    return format(dateObj, 'MMMM d, yyyy');
  };

  const [selectedSong, setSelectedSong] = useState(null);
  const [chordChartOpen, setChordChartOpen] = useState(false);
  const [currentKey, setCurrentKey] = useState('');
  const [chordContent, setChordContent] = useState('');

  const [youtubeDialogOpen, setYoutubeDialogOpen] = useState(false);
  const [selectedYoutubeUrl, setSelectedYoutubeUrl] = useState('');

  const [selectedVideoUrl, setSelectedVideoUrl] = useState('');
  const [selectedVideoTitle, setSelectedVideoTitle] = useState('');
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  useEffect(() => {
    if (selectedSong && currentKey) {
      console.log('Getting transposed chord chart for:', selectedSong);
      const transposed = getTransposedChordChart();
      console.log('Transposed content:', transposed);
      setChordContent(transposed);
    }
  }, [selectedSong, currentKey]);

  const handleViewChordChart = (song) => {
    console.log('Opening chord chart for song:', song);
    
    if (!song || !song.song) {
      console.error('Invalid song data provided to handleViewChordChart');
      console.error('Error: Could not load chord chart');
      return;
    }
    
    console.log('Song default key:', song.song?.defaultKey);
    console.log('Service key:', song?.key);
    setSelectedSong(song);
    setCurrentKey(song?.key || song.song?.defaultKey || 'C');
    setChordChartOpen(true);
  };

  const handleCloseChordChart = async () => {
    if (initialData?._id) {
      try {
        const response = await customFetch.get(`/services/${initialData._id}`);
        if (onServiceUpdate) {
          onServiceUpdate(response.data);
        }
      } catch (error) {
        console.error('Error fetching updated service data:', error);
        console.error('Error updating service data');
      }
    }
    setSelectedSong(null);
    setCurrentKey('');
    setChordChartOpen(false);
  };

  const handleViewYoutube = (url) => {
    setSelectedYoutubeUrl(url);
    setYoutubeDialogOpen(true);
  };

  const handleCloseYoutube = async () => {
    if (initialData?._id) {
      try {
        const response = await customFetch.get(`/services/${initialData._id}`);
        if (onServiceUpdate) {
          onServiceUpdate(response.data);
        }
      } catch (error) {
        console.error('Error fetching updated service data:', error);
        console.error('Error updating service data');
      }
    }
    setYoutubeDialogOpen(false);
    setSelectedYoutubeUrl('');
  };

  const getTransposedChordChart = () => {
    console.log('Getting transposed chord chart...');
    console.log('Selected song:', selectedSong);
    console.log('Current key:', currentKey);
    
    if (!selectedSong?.song?.chordChart?.content) {
      console.log('No chord chart content found');
      return '';
    }
    
    // Use the song's default key as the source, and the current service key as target
    const fromKey = selectedSong.song?.defaultKey || selectedSong.key || 'C';
    const toKey = currentKey || 'C';
    
    if (!fromKey || !toKey) {
      console.log('Missing key information for transposition');
      return selectedSong.song.chordChart.content || '';
    }
    
    console.log('Transposing from:', fromKey, 'to:', toKey);
    
    try {
      const semitones = getSemitones(fromKey, toKey);
      console.log('Semitones to transpose:', semitones);
      
      const transposed = transposeChordChart(selectedSong.song.chordChart.content, semitones);
      console.log('Transposed content:', transposed);
      return transposed;
    } catch (error) {
      console.error('Error transposing chord chart:', error);
      return selectedSong.song.chordChart.content || '';
    }
  };

  const getYoutubeUrl = (song) => {
    console.log('Checking song for streaming links:', song);
    if (!song || !song.streamingLinks || !Array.isArray(song.streamingLinks)) return null;
    const youtubeLink = song.streamingLinks.find(link => link && link.type === 'youtube');
    return youtubeLink?.url;
  };

  const handleStatusChange = async (memberId, newStatus, event, userData = null) => {
    console.log('handleStatusChange called with:', { memberId, newStatus, userData });
    
    // If we don't have a memberId but we have userData, that's okay - we'll handle it in handleUpdateMemberStatus
    if (!memberId && !userData) {
      console.error('Neither member ID nor user data provided for status change');
      console.error('Error updating status: Member information not found');
      return;
    }
    
    if (newStatus === 'declined') {
      // If we have a member ID, use it; otherwise store the userData object
      if (memberId) {
        setMemberToDecline(memberId);
      } else {
        // Store the entire userData object to use later
        setMemberToDecline({ userData });
      }
      setDeclineReason('');
      setDeclineDialogOpen(true);
      return;
    }
    
    await handleUpdateMemberStatus(memberId, newStatus, '', userData, event);
  };

  const handleDeclineSubmit = async () => {
    if (!declineReason.trim() || !memberToDecline) {
      return;
    }

    // Get the decline dialog button element to use for animation positioning
    const dialogElement = document.querySelector('[data-section="decline-dialog"]') || 
                         document.querySelector('.MuiDialog-container');

    // Check if memberToDecline is an object with userData (from the case where we don't have a member ID)
    if (typeof memberToDecline === 'object' && memberToDecline.userData) {
      await handleUpdateMemberStatus(null, 'declined', declineReason.trim(), memberToDecline.userData, { domElement: dialogElement });
    } else {
      // Regular case with a member ID
      await handleUpdateMemberStatus(memberToDecline, 'declined', declineReason.trim(), null, { domElement: dialogElement });
    }
    
    setDeclineDialogOpen(false);
    setMemberToDecline(null);
    setDeclineReason('');
  };

  const handleDeclineCancel = async () => {
    if (initialData?._id) {
      try {
        const response = await customFetch.get(`/services/${initialData._id}`);
        if (onServiceUpdate) {
          onServiceUpdate(response.data);
        }
      } catch (error) {
        console.error('Error fetching updated service data:', error);
        console.error('Error updating service data');
      }
    }
    setDeclineDialogOpen(false);
    setMemberToDecline(null);
    setDeclineReason('');
  };

  // Helper function to find a member ID by user ID
  const findMemberIdByUserId = (userId) => {
    if (!userId || !initialData || !initialData.team) {
      return null;
    }
    
    console.log('Looking for member with user ID:', userId);
    console.log('Available team members:', initialData.team);
    
    const member = initialData.team.find(m => m.user?._id === userId);
    if (member) {
      console.log('Found member:', member);
      return member._id;
    }
    
    return null;
  };

  const handleUpdateMemberStatus = async (memberId, newStatus, reason = '', userData = null, event = null) => {
    try {
      const serviceId = initialData._id;
      if (!serviceId) {
        console.error('Service ID not found');
        return;
      }

      console.log('Updating member status:', {
        serviceId,
        memberId,
        newStatus,
        reason,
        userData
      });

      // If we have a member object without an ID but with a user ID, try to find the member ID first
      if (!memberId && userData && userData.user && userData.user._id) {
        console.log('No member ID provided, but we have user data. Trying to find member by user ID...');
        
        try {
          // First, try to find the member ID in the current service data
          const userId = userData.user._id;
          console.log('Looking for member with user ID:', userId);
          
          // Get the latest service data to ensure we have the most up-to-date team info
          const serviceResponse = await customFetch.get(`/services/${serviceId}`);
          const service = serviceResponse.data;
          console.log('Service data:', service);
          
          // Find the member with matching user ID
          const member = service.team.find(m => m.user?._id === userId);
          
          if (member && member._id) {
            console.log('Found member with ID:', member._id);
            
            // Now use the standard endpoint with the found member ID
            const response = await customFetch.patch(
              API_ENDPOINTS.SERVICES.MEMBER_STATUS(serviceId, member._id),
              {
                status: newStatus,
                declineReason: reason
              }
            );
            
            console.log('Updated status successfully:', response.data);
            
            // Update local state - find the member by user ID
            setFormData(prev => ({
              ...prev,
              team: prev.team.map(m => 
                m.user?._id === userId
                  ? { ...m, status: newStatus, declineReason: reason }
                  : m
              )
            }));
            
            // Update parent component state
            if (onServiceUpdate) {
              onServiceUpdate(response.data);
            }
            
            // Show visual feedback
            showSavedAnimation(event);
            
            return;
          } else {
            console.error('Could not find member with user ID:', userId);
            console.error('Error: Could not find your member record');
          }
        } catch (error) {
          console.error('Error finding member by user ID:', error);
          console.error('Error updating status: ' + (error.response?.data?.msg || error.message));
        }
      }
      
      // If the member doesn't exist yet (no _id), create them first
      if (!memberId) {
        if (!userData) {
          console.error('User data is required for creating new member');
          return;
        }
        
        console.log('Creating new team member...', userData);
        const createResponse = await customFetch.post(
          `/services/${serviceId}/members`,
          {
            user: userData._id, // Send only the user ID
            position: userData.position || '',
            customPosition: userData.customPosition || '',
            status: newStatus,
            declineReason: reason,
            role: userData.role || 'musician'
          }
        );
        
        // Update local state with the new member
        setFormData(prev => ({
          ...prev,
          team: [...prev.team, createResponse.data]
        }));

        // Update parent component state
        if (onServiceUpdate) {
          onServiceUpdate(createResponse.data);
        }

        // Show visual feedback
        showSavedAnimation(event);

        return;
      }

      console.log('Updating member status:', {
        serviceId,
        memberId,
        newStatus,
        reason
      });

      const response = await customFetch.patch(
        API_ENDPOINTS.SERVICES.MEMBER_STATUS(serviceId, memberId),
        {
          status: newStatus,
          declineReason: reason
        }
      );
      
      // Update local state
      setFormData(prev => ({
        ...prev,
        team: prev.team.map(member => 
          member._id === memberId 
            ? { ...member, status: newStatus, declineReason: reason }
            : member
        )
      }));

      // Update parent component state
      if (onServiceUpdate) {
        onServiceUpdate(response.data);
      }

      // Show visual feedback
      showSavedAnimation(event);

      console.log('Status updated successfully:', response.data);
    } catch (error) {
      console.error('Error updating member status:', error);
      console.error('Failed to update member status');
    }
  };

  const formatChordChartLine = (line) => {
    const isGuitarTab = /[|-][0-9-|]+/.test(line);
    const isChordLine = /^[A-G][A-Za-z0-9#\/]*(\s+[A-G][A-Za-z0-9#\/]*)*$/.test(line.trim());
    
    if (isGuitarTab) {
      return (
        <Typography
          component="div"
          sx={{
            fontFamily: 'monospace',
            color: 'purple',
            fontSize: {
              xs: '0.85em',  // Smaller font on mobile
              sm: '0.95em',  // Slightly larger on small tablets
              md: '1.1em',   // Normal size on desktop
            },
            overflowX: {
              xs: 'auto',    // Enable horizontal scroll on mobile if needed
              sm: 'auto',
              md: 'visible'
            },
            whiteSpace: 'nowrap', // Prevent tab lines from breaking
          }}
        >
          {line}
        </Typography>
      );
    }
    
    if (isChordLine) {
      return (
        <Typography
          component="div"
          sx={{
            fontFamily: 'monospace',
            fontWeight: 'bold',
            fontSize: '1.1em',
          }}
        >
          {line}
        </Typography>
      );
    }

    return (
      <Typography
        component="div"
        sx={{
          fontFamily: 'monospace',
          fontSize: '1.1em',
        }}
      >
        {line} 
      </Typography>
    );
  };

  const handleVideoClick = (url, title) => {
    console.log('Opening video:', { url, title });
    
    if (!url) {
      console.error('Invalid URL provided to handleVideoClick');
      console.error('Error: Could not load video');
      return;
    }
    
    setSelectedVideoUrl(url);
    setSelectedVideoTitle(title || 'Untitled Video');
    setVideoModalOpen(true);
  };

  // Update form data and trigger auto-save
  const updateFormData = (updates, event) => {
    const newFormData = typeof updates === 'function'
      ? updates(formData)
      : { ...formData, ...updates };
    
    setFormData(newFormData);
    setLastSavedField(event?.target);
    debouncedSave(newFormData, event);
  };

  // Debounced save function
  const debouncedSave = useCallback(
    debounce(async (data, event) => {
      if (!initialData?._id) return; // Only auto-save if editing existing service
      
      try {
        const safeFormatTime = (timeValue, defaultTime = '00:00') => {
          if (!timeValue) return defaultTime;
          
          if (typeof timeValue === 'string') {
            if (/^\d{1,2}:\d{2}$/.test(timeValue)) return timeValue;
            const parsed = parseISO(timeValue);
            if (isNaN(parsed.getTime())) return defaultTime;
            return format(parsed, 'HH:mm');
          }
          
          if (!(timeValue instanceof Date) || isNaN(timeValue.getTime())) {
            console.warn('Invalid time value:', timeValue);
            return defaultTime;
          }
          
          return format(timeValue, 'HH:mm');
        };

        // Format team data correctly for the server
        const formattedTeam = data.team.map(member => ({
          // If member has an _id, include it
          ...(member._id ? { _id: member._id } : {}),
          // Server expects user as an object with _id property
          user: { _id: member.user._id || member.user },
          position: member.position,
          customPosition: member.customPosition || '',
          status: member.status,
          declineReason: member.declineReason || '',
          role: member.role || 'musician'
        }));

        const submissionData = {
          ...data,
          description: data.description || '',
          date: data.date ? (data.date instanceof Date ? data.date.toISOString() : data.date) : null,
          time: {
            start: safeFormatTime(data.time.start),
            end: safeFormatTime(data.time.end)
          },
          rehearsal: {
            ...data.rehearsal,
            notes: data.rehearsal.notes || '',
            date: data.rehearsal.date ? (data.rehearsal.date instanceof Date ? data.rehearsal.date.toISOString() : data.rehearsal.date) : null,
            time: {
              start: safeFormatTime(data.rehearsal.time.start),
              end: safeFormatTime(data.rehearsal.time.end)
            }
          },
          // Use the properly formatted team data
          team: formattedTeam
        };

        // If we're updating a team member's status
        const updatedMember = data.team.find(member => 
          member.status !== initialData.team.find(m => m.user._id === member.user._id)?.status
        );

        if (updatedMember) {
          // Use the member status endpoint
          const response = await customFetch.patch(
            API_ENDPOINTS.SERVICES.MEMBER_STATUS(initialData._id, updatedMember.user._id),
            {
              status: updatedMember.status,
              declineReason: updatedMember.declineReason || ''
            }
          );
          showSavedAnimation(event);
          
          if (onServiceUpdate) {
            onServiceUpdate(response.data);
          }
        } else {
          // Regular service update
          console.log('Updating service with data:', {
            url: API_ENDPOINTS.SERVICES.UPDATE_SERVICE(initialData._id),
            data: submissionData
          });
          try {
            const response = await customFetch.patch(
              API_ENDPOINTS.SERVICES.UPDATE_SERVICE(initialData._id), 
              submissionData
            );
            console.log('Service update response:', response);
            showSavedAnimation(event);
            
            if (onServiceUpdate) {
              onServiceUpdate(response.data);
            }
          } catch (error) {
            console.error('Service update failed:', {
              error,
              response: error.response,
              data: error.response?.data,
              status: error.response?.status,
              message: error.message
            });
            throw error; // Re-throw to be caught by the outer catch block
          }
        }
      } catch (error) {
        console.error('Auto-save failed:', error);
        console.error(error.response?.data?.message || error.message || 'Failed to save changes');
      }
    }, 1000),
    [initialData, customFetch, onServiceUpdate]
  );

  const handleCreateService = () => {
    // Helper function to safely format time
    const safeFormatTime = (timeValue, defaultTime = '00:00') => {
      if (!timeValue) return defaultTime;
      if (!(timeValue instanceof Date) || isNaN(timeValue.getTime())) {
        console.warn('Invalid time value:', timeValue);
        return defaultTime;
      }
      // Handle both Date objects and ISO strings
      const timeObj = timeValue instanceof Date ? timeValue : parseISO(timeValue);
      return format(timeObj, 'HH:mm');
    };

    // Format team data correctly for the server
    const formattedTeam = formData.team.map(member => ({
      // If member has an _id, include it
      ...(member._id ? { _id: member._id } : {}),
      // Server expects user as an object with _id property
      user: { _id: member.user._id || member.user },
      position: member.position,
      customPosition: member.customPosition || '',
      status: member.status,
      declineReason: member.declineReason || '',
      role: member.role || 'member'
    }));

    const submissionData = {
      ...formData,
      description: formData.description || '',
      date: formData.date ? (formData.date instanceof Date ? formData.date.toISOString() : formData.date) : null,
      time: {
        start: safeFormatTime(formData.time.start),
        end: safeFormatTime(formData.time.end)
      },
      rehearsal: {
        ...formData.rehearsal,
        notes: formData.rehearsal.notes || '',
        date: formData.rehearsal.date ? (formData.rehearsal.date instanceof Date ? formData.rehearsal.date.toISOString() : formData.rehearsal.date) : null,
        time: {
          start: safeFormatTime(formData.rehearsal.time.start),
          end: safeFormatTime(formData.rehearsal.time.end)
        }
      },
      // Use the properly formatted team data
      team: formattedTeam
    };

    onSubmit(submissionData);
  };

  const handleClose = async () => {
    if (initialData?._id) {
      try {
        const response = await customFetch.get(`/services/${initialData._id}`);
        if (onServiceUpdate) {
          onServiceUpdate(response.data);
        }
      } catch (error) {
        console.error('Error fetching updated service data:', error);
        console.error('Error updating service data');
      }
    }
    onClose();
  };

  // Helper: format a team member for the backend API
  const formatMemberForApi = (member) => {
    const userId = typeof member.user === 'object' ? (member.user?._id || member.user?.id) : member.user;
    return {
      user: { _id: userId },
      position: member.position || '',
      customPosition: member.customPosition || '',
      status: member.status || 'pending',
      declineReason: member.declineReason || '',
      role: member.role || 'musician'
    };
  };

  // Helper: build the team-only payload for the backend
  const buildTeamUpdatePayload = (teamArray) => {
    return {
      team: teamArray.map(formatMemberForApi)
    };
  };

  const handleTeamChange = async (newTeam, newMemberData = null) => {
    try {
      const serviceId = initialData?._id;
      
      // For new services (not yet saved), just update local state
      if (!serviceId) {
        setFormData(prev => ({
          ...prev,
          team: newTeam
        }));
        return;
      }
      
      // Check if a team member was removed by comparing lengths
      if (newTeam.length < formData.team.length) {
        console.log('Team member removal detected');
        
        // Update local state first
        updateFormData(prev => ({
          ...prev,
          team: newTeam
        }));
        
        // Send only team data to the backend
        const payload = buildTeamUpdatePayload(newTeam);
        console.log('Sending team update after member removal:', payload);
        
        const response = await customFetch.patch(
          API_ENDPOINTS.SERVICES.UPDATE_SERVICE(serviceId),
          payload
        );
        
        console.log('Service updated successfully after member removal:', response.data);
        
        if (onServiceUpdate) {
          onServiceUpdate(response.data);
        }
        return;
      }
      
      // If we have a new member to add
      if (newMemberData) {
        console.log('Adding new team member:', newMemberData);
        
        try {
          // Build team array: existing members + new member
          const allMembers = [...formData.team, { ...newMemberData, user: { _id: newMemberData.user } }];
          const payload = buildTeamUpdatePayload(allMembers);
          
          console.log('Sending team update with new member:', payload);
          
          const response = await customFetch.patch(
            API_ENDPOINTS.SERVICES.UPDATE_SERVICE(serviceId),
            payload
          );
          
          console.log('Member added successfully:', response.data);
          
          // Update local state with the server response
          updateFormData(prev => ({
            ...prev,
            team: response.data.team || prev.team
          }));
          
          if (onServiceUpdate) {
            onServiceUpdate(response.data);
          }
          return;
        } catch (error) {
          console.error('Failed to add team member:', error);
          console.error('Error details:', error.response?.data);
          return;
        }
      }

      // Find what changed between the original team and the new team
      const originalTeam = formData.team || [];
      
      // Check if this is just a status update for a single member
      const statusUpdates = newTeam.filter((newMember, index) => {
        if (index < originalTeam.length) {
          const originalMember = originalTeam[index];
          return (
            newMember._id && 
            originalMember._id === newMember._id && 
            (newMember.status !== originalMember.status || 
             newMember.declineReason !== originalMember.declineReason)
          );
        }
        return false;
      });

      console.log('Members with status changes:', statusUpdates);
      
      // If we have members with status changes, use a minimal service update
      if (statusUpdates.length > 0) {
        try {
          // Get the member that was updated
          const updatedMember = statusUpdates[0]; // Just handle one at a time for simplicity
          console.log(`Updating status for member ${updatedMember._id} to ${updatedMember.status}`);
          
          // Instead of updating the entire service, let's use a dedicated endpoint for status updates
          // This is a much simpler approach that avoids validation issues
          console.log(`Updating status for member ${updatedMember._id} to ${updatedMember.status}`);
          
          try {
            // Use the member status endpoint instead of updating the whole service
            const response = await customFetch.patch(
              API_ENDPOINTS.SERVICES.MEMBER_STATUS(serviceId, updatedMember.user._id),
              {
                status: updatedMember.status,
                declineReason: updatedMember.status === 'declined' ? (updatedMember.declineReason || '') : ''
              }
            );
            
            console.log('Status update successful via dedicated endpoint:', response.data);
            
            // Update the local state with just the status change
            updateFormData(prev => {
              const updatedTeam = prev.team.map(member => {
                if (member._id === updatedMember._id) {
                  return {
                    ...member,
                    status: updatedMember.status,
                    declineReason: updatedMember.status === 'declined' ? updatedMember.declineReason : ''
                  };
                }
                return member;
              });
              
              return {
                ...prev,
                team: updatedTeam
              };
            });
            
            // Notify parent component with the updated team
            if (onServiceUpdate) {
              onServiceUpdate(response.data);
            }
            
            // Show success message with the member's name if available
            const memberName = updatedMember.user.firstName && updatedMember.user.lastName
              ? `${updatedMember.user.firstName} ${updatedMember.user.lastName}`
              : 'team member';
              
            console.log(`Status updated for ${memberName}!`);
            return;
          } catch (error) {
            console.error('Failed to update member status via dedicated endpoint:', error);
            console.log('Falling back to full service update method...');
          }
          
          // Fallback: If the dedicated endpoint fails, try updating the whole service
          // Create a minimal service update with only the necessary fields
          const minimalServiceUpdate = {
            _id: serviceId, // Include the service ID
            title: formData.title || 'Service', // Required field
            date: formData.date ? (formData.date instanceof Date ? formData.date.toISOString() : formData.date) : new Date().toISOString(), // Required field
            // Include songs array (required by server)
            songs: formData.songs ? formData.songs.map(song => ({
              song: song.song?._id || song.song,
              key: song.key || ''
            })) : [],
            // Include minimal time fields to avoid validation errors
            time: {
              start: formData.time?.start ? (formData.time.start instanceof Date ? formData.time.start.toISOString() : formData.time.start) : new Date().toISOString(),
              end: formData.time?.end ? (formData.time.end instanceof Date ? formData.time.end.toISOString() : formData.time.end) : new Date(new Date().getTime() + 60 * 60 * 1000).toISOString()
            },
            // Include minimal rehearsal time fields with required date
            rehearsal: {
              date: formData.rehearsal?.date ? (formData.rehearsal.date instanceof Date ? formData.rehearsal.date.toISOString() : formData.rehearsal.date) : new Date().toISOString(), // Required field
              time: {
                start: formData.rehearsal?.time?.start ? (formData.rehearsal.time.start instanceof Date ? formData.rehearsal.time.start.toISOString() : formData.rehearsal.time.start) : new Date().toISOString(),
                end: formData.rehearsal?.time?.end ? (formData.rehearsal.time.end instanceof Date ? formData.rehearsal.time.end.toISOString() : formData.rehearsal.time.end) : new Date(new Date().getTime() + 60 * 60 * 1000).toISOString()
              },
              notes: formData.rehearsal?.notes || ''
            },
            team: formData.team.map(member => {
              // Only update the status for the changed member
              if (member._id === updatedMember._id) {
                return {
                  _id: member._id,
                  user: { _id: member.user._id },
                  position: member.position,
                  customPosition: member.customPosition || '',
                  status: updatedMember.status,
                  declineReason: updatedMember.status === 'declined' ? (updatedMember.declineReason || '') : '',
                  role: member.role || 'musician'
                };
              }
              // For other members, just include minimal required info
              return {
                _id: member._id,
                user: { _id: member.user._id },
                position: member.position,
                status: member.status,
                role: member.role || 'musician'
              };
            })
          };
          
          console.log('Sending minimal service update:', minimalServiceUpdate);
          
          try {
            // Update the service with minimal data
            const response = await customFetch.patch(
              API_ENDPOINTS.SERVICES.UPDATE_SERVICE(serviceId),
              minimalServiceUpdate
            );
            
            if (response.data) {
              console.log('Status update successful:', response.data);
              
              // Update the local state with just the status change
              // This avoids a full refresh and keeps the current form state
              updateFormData(prev => {
                const updatedTeam = prev.team.map(member => {
                  if (member._id === updatedMember._id) {
                    return {
                      ...member,
                      status: updatedMember.status,
                      declineReason: updatedMember.status === 'declined' ? updatedMember.declineReason : ''
                    };
                  }
                  return member;
                });
                
                return {
                  ...prev,
                  team: updatedTeam
                };
              });
              
              // Notify parent component with the updated team
              if (onServiceUpdate) {
                onServiceUpdate(response.data);
              }
              
              // Show success message with the member's name if available
              const memberName = updatedMember.user.firstName && updatedMember.user.lastName
                ? `${updatedMember.user.firstName} ${updatedMember.user.lastName}`
                : 'team member';
                
              console.log(`Status updated for ${memberName}!`);
            }
          } catch (error) {
            console.error('Failed to update service with minimal data:', error);
            console.error('Error details:', error.response?.data);
            
            // Don't show error toast since the status was already updated successfully
            // through the dedicated endpoint
          }
        } catch (error) {
          console.error('Failed to update team status:', error);
          console.error('Failed to update team status: ' + (error.response?.data?.message || error.message));
        }
        
        return;
      }

      // For other updates (position changes, etc.), use the regular service update endpoint
      const serviceUpdate = {
        ...formData,
        team: newTeam.map(member => ({
          // If member has an _id, include it
          ...(member._id ? { _id: member._id } : {}),
          // Server expects user as an object with _id property
          user: { _id: member.user._id },
          position: member.position,
          customPosition: member.customPosition || '',
          status: member.status,
          declineReason: member.declineReason || '',
          role: member.role || 'musician'
        }))
      };

      console.log('Updating service with team (full update):', serviceUpdate.team);

      // Update the service
      const response = await customFetch.patch(
        API_ENDPOINTS.SERVICES.UPDATE_SERVICE(serviceId),
        serviceUpdate
      );

      if (response.data) {
        // Update local state
        updateFormData(prev => ({
          ...prev,
          ...response.data
        }));

        // Notify parent component
        if (onServiceUpdate) {
          onServiceUpdate(response.data);
        }
      }
    } catch (error) {
      console.error('Failed to update team:', error);
      console.error('Failed to update team: ' + (error.response?.data?.message || error.message));
    }
  };

  const renderViewMode = () => (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3}>

        {formData.description && (
          <Grid item xs={12}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderRadius: 2
              }}
            >
              <Typography variant="subtitle2" color="primary" gutterBottom>Description</Typography>
              <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                {formData.description} 
              </Typography>
            </Paper>
          </Grid>
        )}


        <Grid item xs={12} md={8}>
          <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
            Songs
          </Typography>
          {formData.songs.length > 0 ? (
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderRadius: 2
              }}
            >
              {formData.songs.map((song, index) => {
                console.log('Song data:', song);
                return (
                  <Box 
                    key={song._id} 
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.5,
                      '&:not(:last-child)': {
                        borderBottom: 1,
                        borderColor: 'divider'
                      }
                    }}
                  >
                    <Typography variant="body1">
                      <Box component="span" sx={{ 
                        fontWeight: 'bold', 
                        color: 'purple',
                        mr: 1,
                        fontSize: '1.2rem'
                      }}>
                        {song?.key || 'C'}
                      </Box>
                      - {song?.song?.title || 'Untitled Song'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {song?.song && getYoutubeUrl(song.song) && (
                        <IconButton
                          size="small"
                          onClick={() => handleVideoClick(getYoutubeUrl(song.song), song?.song?.title || 'Untitled Song')}
                          sx={{
                            color: '#9C27B0', // Material-UI purple
                          }}
                        >
                          <PlayCircleIcon fontSize="small" />
                        </IconButton>
                      )}
                      {song?.song?.chordChart && (
                        <IconButton
                          size="small"
                          onClick={() => handleViewChordChart(song)}
                          sx={{ color: theme.palette.primary.main }}
                        >
                          <MusicNoteIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Paper>
          ) : (
            <Typography variant="body2" color="text.secondary">No songs added</Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
            Team Members
          </Typography>
          {formData.team.length > 0 ? (
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderRadius: 2
              }}
            >
              {/* Show declined members with reasons at the top */}
              {formData.team.some(member => member.status === 'declined') && (
                <Box 
                  sx={{ 
                    mb: 2,
                    p: 1.5,
                    bgcolor: alpha(theme.palette.error.main, 0.05),
                    borderRadius: 1,
                    border: 1,
                    borderColor: alpha(theme.palette.error.main, 0.1),
                  }}
                >
                  {formData.team
                    .filter(member => member.status === 'declined')
                    .map((member) => (
                      <Box 
                        key={member._id}
                        sx={{ 
                          mb: 1,
                          '&:last-child': { mb: 0 }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {member.user?.firstName} {member.user?.lastName} Declined:
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="error.dark"
                          sx={{ 
                            fontStyle: 'italic',
                            pl: 2,
                            mt: 0.5,
                            bgcolor: alpha(theme.palette.error.main, 0.05),
                            py: 1,
                            px: 2,
                            borderRadius: 1,
                          }}
                        >
                          {member.declineReason ? member.declineReason : t('services.noDeclineReason')}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              )}

              {/* Team member list */}
              {formData.team.map((member) => (
                <Box 
                  key={member._id} 
                  sx={{ 
                    py: 1,
                    '&:not(:last-child)': {
                      borderBottom: 1,
                      borderColor: 'divider'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Box sx={{ pt: 0.5 }}>
                        <Tooltip title={getRoleIcon(member.role).tooltip}>
                          <IconButton
                            size="small"
                            sx={{
                              color: getRoleIcon(member.role).color,
                              padding: '4px',
                            }}
                          >
                            {getRoleIcon(member.role).icon}
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Box>
                        <Typography variant="body1">
                          {member.user?.firstName} {member.user?.lastName}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ display: 'block', mt: -0.5 }}
                        >
                          {member.customPosition || member.position}
                        </Typography>
                        
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {member.user?._id === user?._id || canEdit() ? (
                        <FormControl size="small">
                          <Select
                            value={member.status}
                            onChange={(e) => {
                            console.log('Status change triggered for member:', member);
                            
                            // If we have a member ID, use it directly
                            if (member._id) {
                              handleStatusChange(member._id, e.target.value, e);
                              return;
                            }
                            
                            // If we don't have a member ID but have user data, pass the whole member object
                            if (member.user && member.user._id) {
                              // Pass the member object as userData instead of memberId
                              handleStatusChange(null, e.target.value, e, member);
                              return;
                            }
                            
                            // If we can't identify the member at all
                            console.error('Cannot determine member identity:', member);
                            console.error('Error: Cannot update status - unable to identify member');
                          }}
                            size="small"
                            sx={{
                              minWidth: 100,
                              '& .MuiSelect-select': {
                                py: 0.5,
                              },
                              ...(member.status === 'confirmed' && {
                                color: theme.palette.success.main,
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: alpha(theme.palette.success.main, 0.5),
                                }
                              }),
                              ...(member.status === 'declined' && {
                                color: theme.palette.error.main,
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: alpha(theme.palette.error.main, 0.5),
                                },
                                animation: 'declinedPulse 2s infinite',
                                ...declinedPulseAnimation,
                              }),
                              ...(member.status === 'pending' && {
                                color: theme.palette.warning.main,
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: alpha(theme.palette.warning.main, 0.5),
                                },
                                animation: 'pendingPulse 2s infinite',
                                ...pendingPulseAnimation,
                              })
                            }}
                          >
                            <MenuItem value="pending">{t('services.status.pending')}</MenuItem>
                            <MenuItem value="confirmed">{t('services.status.confirmed')}</MenuItem>
                            <MenuItem value="declined">{t('services.status.declined')}</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Chip
                          size="small"
                          label={member.status}
                          color={
                            member.status === 'confirmed' ? 'success' :
                            member.status === 'declined' ? 'error' :
                            'default'
                          }
                          sx={{ 
                            textTransform: 'capitalize',
                            ...(member.status === 'pending' && {
                              bgcolor: '#FFF3BF',
                              color: '#997404',
                              '& .MuiChip-label': {
                                color: '#997404'
                              }
                            })
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Paper>
          ) : (
            <Typography variant="body2" color="text.secondary">No team members assigned</Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
        </Grid>

        <Grid item xs={12} md={8.4}>
          <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
            Service Details
          </Typography>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2, 
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderRadius: 2
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>Date</Typography>
              <Typography variant="body1">{formatDate(formData.date)}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>Service Time</Typography>
              <Typography variant="body1">
                {formatTime(formData.time.start)} - {formatTime(formData.time.end)}
              </Typography>
            </Box>

            {formData.description && (
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>Description</Typography>
                <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                  {formData.description} 
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={3.6}>
          <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
            Rehearsal Details
          </Typography>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2, 
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderRadius: 2
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>Date</Typography>
              <Typography variant="body1">{formatDate(formData.rehearsal.date)}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>Time</Typography>
              <Typography variant="body1">
                {formatTime(formData.rehearsal.time.start)} - {formatTime(formData.rehearsal.time.end)}
              </Typography>
            </Box>

            {formData.rehearsal.notes && (
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>Notes</Typography>
                <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                  {formData.rehearsal.notes}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderEditMode = () => (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          {/* Basic Info */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => updateFormData(prev => ({ ...prev, title: e.target.value }), e)}
              required
            />
          </Grid>

          {/* Songs Section */}
          <Grid item xs={12} md={8} data-section="songs">
            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main, fontWeight: 600 }}>
              Songs
            </Typography>
            <ServiceSongList
              songs={formData.songs}
              onSongsChange={(newSongs, domElement) => updateFormData(
                prev => ({ ...prev, songs: newSongs }), 
                { domElement }
              )}
              customFetch={customFetch}
              onVideoClick={handleVideoClick}
              readOnly={readOnly}
            />
          </Grid>

          {/* Team Section */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main, fontWeight: 600 }}>
              Team
            </Typography>
            {/* Show declined members with reasons at the top */}
            {formData.team.some(member => member.status === 'declined') && (
              <Box 
                sx={{ 
                  mb: 2,
                  p: 1.5,
                  bgcolor: alpha(theme.palette.error.main, 0.05),
                  borderRadius: 1,
                  border: 1,
                  borderColor: alpha(theme.palette.error.main, 0.1),
                }}
              >
                {formData.team
                  .filter(member => member.status === 'declined')
                  .map((member) => (
                    <Box 
                      key={member._id}
                      sx={{ 
                        mb: 1,
                        '&:last-child': { mb: 0 }
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {member.user?.firstName} {member.user?.lastName} declined:
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="error.dark"
                        sx={{ 
                          fontStyle: 'italic',
                          pl: 2,
                          mt: 0.5,
                          bgcolor: alpha(theme.palette.error.main, 0.05),
                          py: 1,
                          px: 2,
                          borderRadius: 1,
                        }}
                      >
                        {member.declineReason ? member.declineReason : t('services.noDeclineReason')}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            )}
            <TeamScheduler
              team={formData.team}
              onTeamChange={handleTeamChange}
              customFetch={customFetch}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Service & Rehearsal Details */}
          <Grid item xs={12} md={8.4}>
            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main, fontWeight: 600 }}>
              {t('services.serviceDetails')}
            </Typography>
            <Box sx={{ mb: 2 }}>
              <DatePicker
                label={t('services.date')}
                value={formData.date}
                onChange={(value) => updateFormData(prev => ({ ...prev, date: value }))}
                renderInput={(params) => <TextField {...params} fullWidth required />}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TimePicker
                label={t('services.rehearsal.startTime')}
                value={formData.time.start}
                onChange={(value) => updateFormData(prev => ({ ...prev, time: { ...prev.time, start: value } }))}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
              <TimePicker
                label={t('services.rehearsal.endTime')}
                value={formData.time.end}
                onChange={(value) => updateFormData(prev => ({ ...prev, time: { ...prev.time, end: value } }))}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Box>
            <TextField
              fullWidth
              label={t('services.description')}
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => updateFormData(prev => ({ ...prev, description: e.target.value }), e)}
            />
          </Grid>

          <Grid item xs={12} md={3.6}>
            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main, fontWeight: 600 }}>
              {t('services.rehearsal.title')}
            </Typography>
            <Box sx={{ mb: 2 }}>
              <DatePicker
                label={t('services.rehearsal.date')}
                value={formData.rehearsal.date}
                onChange={(value) => updateFormData(prev => ({ ...prev, rehearsal: { ...prev.rehearsal, date: value } }))}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TimePicker
                label={t('services.rehearsal.startTime')}
                value={formData.rehearsal.time.start}
                onChange={(value) => updateFormData(prev => ({ ...prev, rehearsal: { ...prev.rehearsal, time: { ...prev.rehearsal.time, start: value } } }))}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
              <TimePicker
                label={t('services.rehearsal.endTime')}
                value={formData.rehearsal.time.end}
                onChange={(value) => updateFormData(prev => ({ ...prev, rehearsal: { ...prev.rehearsal, time: { ...prev.rehearsal.time, end: value } } }))}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Box>
            <TextField
              fullWidth
              label={t('services.rehearsal.notes')}
              multiline
              rows={2}
              value={formData.rehearsal.notes}
              onChange={(e) => updateFormData(prev => ({ 
                ...prev, 
                rehearsal: { 
                  ...prev.rehearsal, 
                  notes: e.target.value 
                } 
              }), e)}
            />
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );

  const renderViewModeTeam = () => (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
            Team Member
          </Typography>
          {formData.team.length > 0 ? (
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderRadius: 2
              }}
            >
              {/* Show declined members with reasons at the top */}
              {formData.team.some(member => member.status === 'declined') && (
                <Box 
                  sx={{ 
                    mb: 2,
                    p: 1.5,
                    bgcolor: alpha(theme.palette.error.main, 0.05),
                    borderRadius: 1,
                    border: 1,
                    borderColor: alpha(theme.palette.error.main, 0.1),
                  }}
                >
                  {formData.team
                    .filter(member => member.status === 'declined')
                    .map((member) => (
                      <Box 
                        key={member._id}
                        sx={{ 
                          mb: 1,
                          '&:last-child': { mb: 0 }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {member.user?.firstName} {member.user?.lastName} declined:
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="error.dark"
                          sx={{ 
                            fontStyle: 'italic',
                            pl: 2,
                            mt: 0.5,
                            bgcolor: alpha(theme.palette.error.main, 0.05),
                            py: 1,
                            px: 2,
                            borderRadius: 1,
                          }}
                        >
                          {member.declineReason ? member.declineReason : t('services.noDeclineReason')}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              )}

              {/* Team member list */}
              {formData.team.map((member) => (
                <Box 
                  key={member._id}
                  sx={{ 
                    py: 1,
                    '&:not(:last-child)': {
                      borderBottom: 1,
                      borderColor: 'divider'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Box sx={{ pt: 0.5 }}>
                        <Tooltip title={getRoleIcon(member.role).tooltip}>
                          <IconButton
                            size="small"
                            sx={{
                              color: getRoleIcon(member.role).color,
                              padding: '4px',
                            }}
                          >
                            {getRoleIcon(member.role).icon}
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Box>
                        <Typography variant="body1">
                          {member.user?.firstName} {member.user?.lastName}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ display: 'block', mt: -0.5 }}
                        >
                          {member.customPosition || member.position}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {(member.status === 'pending' && member.user?._id === user?._id) ? (
                        <>
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => handleUpdateMemberStatus(member._id, 'confirmed')}
                            sx={{ 
                              bgcolor: alpha(theme.palette.success.main, 0.1),
                              animation: 'pulse 2s infinite',
                              ...pulseAnimation,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.success.main, 0.2),
                                animation: 'none'
                              }
                            }}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleStatusChange(member._id, 'declined')}
                            sx={{ 
                              bgcolor: alpha(theme.palette.error.main, 0.1),
                              transform: 'scale(0.85)',
                              '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.2)
                              }
                            }}
                          >
                            <CancelIcon />
                          </IconButton>
                        </>
                      ) : (
                        <Chip
                          size="small"
                          label={member.status}
                          color={
                            member.status === 'confirmed' ? 'success' :
                            member.status === 'declined' ? 'error' :
                            'default'
                          }
                          sx={{ 
                            textTransform: 'capitalize',
                            ...(member.status === 'pending' && {
                              bgcolor: '#FFF3BF',
                              color: '#997404',
                              '& .MuiChip-label': {
                                color: '#997404'
                              }
                            })
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Paper>
          ) : (
            <Typography variant="body2" color="text.secondary">No team members assigned</Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={!isDesktop}
    >
      <VideoModal
        open={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        videoUrl={selectedVideoUrl}
        title={selectedVideoTitle}
      />
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
          <Typography variant="h6" component="div" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
            {initialData ? formData.title : 'New Service'}
          </Typography>
          {formData.date && (
            <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
              {formData.date ? format(formData.date instanceof Date ? formData.date : parseISO(formData.date), 'MMMM d, yyyy') : ''}
            </Typography>
          )}
          {!readOnly && saveStatus && (
            <Typography 
              variant="body2" 
              sx={{ 
                color: saveStatus === 'Saved' ? 'success.main' : 
                       saveStatus === 'Saving...' ? 'text.secondary' : 'error.main',
                ml: 2
              }}
            >
              {saveStatus}
            </Typography>
          )}
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {readOnly ? renderViewMode() : renderEditMode()}
        {showSaveAnimation && <SavedIndicator />}
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          px: 3,
          py: 2
        }}
      >
        {!initialData && (
          <Button
            onClick={handleCreateService}
            variant="contained"
            color="primary"
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            Create Service
          </Button>
        )}
        <Button
          onClick={handleClose}
          variant={initialData ? "contained" : "text"}
          color={initialData ? "primary" : "inherit"}
          sx={initialData ? {
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          } : {}}
        >
          Close
        </Button>
      </DialogActions>

      {/* Chord Chart Dialog */}
      <Dialog
        open={chordChartOpen}
        onClose={handleCloseChordChart}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {selectedSong?.song?.title} - Chord Chart
          </Typography>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Key</InputLabel>
            <Select
              value={currentKey}
              onChange={(e) => {
                setCurrentKey(e.target.value);
              }}
              label="Key"
            >
              {NOTES.map((note) => (
                <MenuItem key={note} value={note}>
                  {note}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogTitle>
        <DialogContent>
          {chordContent?.split('\n').map((line, index) => (
            <React.Fragment key={index}>
              {formatChordChartLine(line)}
            </React.Fragment>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChordChart}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* YouTube Dialog */}
      <Dialog
        open={youtubeDialogOpen}
        onClose={handleCloseYoutube}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {selectedSong?.song?.title} - YouTube
          </Typography>
        </DialogTitle>
        <DialogContent>
          <iframe
            title="YouTube Video"
            src={selectedYoutubeUrl}
            frameBorder="0"
            allowFullScreen
            width="100%"
            height="315"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseYoutube}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Decline Dialog */}
      <Dialog
        open={declineDialogOpen}
        onClose={handleDeclineCancel}
        maxWidth="xs"
        fullWidth
        data-section="decline-dialog"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Decline Reason
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              autoFocus
              fullWidth
              multiline
              rows={3}
              label="Reason for declining"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              error={declineDialogOpen && !declineReason.trim()}
              helperText={declineDialogOpen && !declineReason.trim() ? "Please provide a reason for declining" : ""}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeclineCancel}>Cancel</Button>
          <Button 
            onClick={handleDeclineSubmit}
            disabled={!declineReason.trim()}
            variant="contained"
            color="primary"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ServiceForm;
